/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export default class SearchConfig {
	static _config: Record<string, any>;
	static tabsConfig = {
		all: {
			label: 'app.search.tab.all',
			categories: {
				apps: {
					title: 'app.search.apps.title',
				},
				quickActions: {
					title: 'app.search.quickActions.title',
					limit: 40,
					action: {
						type: 'link',
						link: '',
					},
				},
				tutorials: {
					title: 'app.search.support.tutorials.title',
					limit: 40,
					action: {
						type: 'link',
						link: '',
					},
				},
				sparkTemplates: {
					title: 'app.search.adobeexpress_templates.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				helpCenter: {
					title: 'app.search.support.title',
					limit: 4,
					action: {
						type: 'link',
						link: '',
					},
				},
				forums: {
					title: 'app.search.forums.title',
					limit: 4,
					action: {
						type: 'link',
						link: '',
					},
				},
				stock: {
					title: 'app.search.stock.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				stockassets: {
					title: 'app.search.stock.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				stocktemplates: {
					title: 'app.search.stock.templates.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				templates: {
					title: 'app.search.stock.templates.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				fonts: {
					title: 'app.search.fonts.title',
					action: {
						type: 'link',
						link: '',
					},
				},
				plugins: {
					title: 'app.search.plugins.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				productInformation: {
					title: 'app.search.productinformation.title',
					limit: 4,
					action: {
						type: 'link',
						link: '',
					},
				},
				yourWork: {
					title: 'app.search.tab.files',
					limit: 7,
					action: {
						type: 'tab',
						link: 'yourWork',
					},
				},
			},
		},
		apps: {
			label: 'app.search.tab.apps',
			categories: {
				apps: {
					title: 'app.search.apps.title',
				},
				productInformation: {
					title: 'app.search.productinformation.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
			},
		},
		help: {
			label: 'app.search.tab.help',
			categories: {
				alltutorials: {
					title: 'app.search.support.tutorials.title',
					limit: 40,
				},
				tutorials: {
					title: 'app.search.support.tutorials.title',
					limit: 40,
					action: {
						type: 'link',
						link: '',
					},
				},
				helpCenter: {
					title: 'app.search.support.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				forums: {
					title: 'app.search.forums.title',
					limit: 4,
					action: {
						type: 'link',
						link: '',
					},
				},
			},
		},
		stock: {
			label: 'app.search.tab.stockMarketplace',
			categories: {
				stock: {
					title: 'app.search.stock.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				stockassets: {
					title: 'app.search.stock.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				stocktemplates: {
					title: 'app.search.stock.templates.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				templates: {
					title: 'app.search.stock.templates.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				plugins: {
					title: 'app.search.plugins.title',
					limit: 10,
					action: {
						type: 'link',
						link: '',
					},
				},
				fonts: {
					title: 'app.search.fonts.title',
					action: {
						type: 'link',
						link: '',
					},
				},
			},
		},
		learn: {
			label: 'app.search.tab.learn',
			categories: {
				tutorials: {
					title: 'app.search.support.tutorials.title',
					limit: 40,
					paginated: true,
				},
			},
		},
		support: {
			label: 'app.search.tab.support',
			categories: {
				helpCenter: {
					title: 'app.search.support.title',
					limit: 40,
					paginated: true,
				},
			},
		},
		community: {
			label: 'app.search.tab.community',
			categories: {
				forums: {
					title: 'app.search.forums.title',
					limit: 40,
					paginated: true,
				},
			},
		},
		productInformation: {
			label: 'app.search.tab.productInfo',
			categories: {
				productInformation: {
					title: 'app.search.productinformation.title',
					limit: 4,
					paginated: true,
				},
			},
		},
		yourWork: {
			label: 'app.search.tab.files',
			categories: {},
		},
	};

	static setSearchConfig(searchConfig: Record<string, any>) {
		SearchConfig._config = searchConfig;
	}

	static getSearchConfig() {
		return SearchConfig._config;
	}

	static getSearchAnalyticsConfig() {
		return SearchConfig._config?.analytics;
	}

	static getSearchConfigForCategory(
		category: string,
		pagination?: { limit?: number; start?: number },
	): searchRequestBody {
		const searchPayload = SearchConfig._config.search;
		const { qisV2, plugins, quickActions, sparkTemplates, experienceId, searchBodies, ...rest } = searchPayload;
		const categorySearchConfig: searchRequestBody = {
			...rest,
			searchBodies: [],
		};
		switch (category.toLowerCase()) {
			case 'apps': {
				categorySearchConfig.qisV2 = qisV2;
				categorySearchConfig.experienceId = experienceId;
				let searchBodiesAppsPayload = searchBodies.find(
					(searchBody) => searchBody?.id?.toLowerCase() === category.toLowerCase(),
				);
				searchBodiesAppsPayload = { ...searchBodiesAppsPayload, ...pagination };
				categorySearchConfig.searchBodies.push(searchBodiesAppsPayload);
				break;
			}
			case 'stock': {
				let searchBodiesStockPayload = searchBodies.find(
					(searchBody) =>
						searchBody?.ussVersion === 'v2' && searchBody?.scope?.includes(category.toLowerCase()),
				);
				searchBodiesStockPayload = { ...searchBodiesStockPayload, ...pagination };
				categorySearchConfig.searchBodies.push(searchBodiesStockPayload);
				break;
			}
			case 'stockassets':
			case 'stocktemplates':
			case 'tutorials':
			case 'adobeforum':
			case 'learn':
			case 'forums':
			case 'helpcenter':
			case 'productinformation': {
				categorySearchConfig.experienceId = experienceId;
				let searchBodiesHelpPayload = searchBodies.find(
					(searchBody) => searchBody?.id?.toLowerCase() === category.toLowerCase(),
				);
				searchBodiesHelpPayload = { ...searchBodiesHelpPayload, ...pagination };
				categorySearchConfig.searchBodies.push(searchBodiesHelpPayload);
				break;
			}
			case 'sparktemplates': {
				categorySearchConfig.sparkTemplates = sparkTemplates;
				break;
			}
			case 'plugins': {
				categorySearchConfig.plugins = plugins;
				break;
			}
			case 'quickactions': {
				categorySearchConfig.quickActions = quickActions;
				break;
			}
			case 'default': {
				return searchPayload;
			}
		}
		return categorySearchConfig;
	}

	static fetchSRPTabs(): Array<string> {
		const tabs = SearchConfig._config?.ui?.tabs ?? [];
		const newTabs = Object.keys(SearchConfig._config?.ui?.tabSections ?? {});
		return newTabs.length ? newTabs : tabs;
	}

	static fetchSRPTabDetails(tab: string): Record<string, any> {
		const tabs = SearchConfig._config?.ui?.tabSections ?? SearchConfig.tabsConfig;
		const tabDetails = tabs[tab];
		return tabDetails;
	}

	static fetchTabCategories(tab: string): Array<string> {
		const tabSections = SearchConfig._config?.ui?.tabSections ?? SearchConfig.tabsConfig;
		const tabSection = tabSections[tab];
		const categories = Object.keys(tabSection.categories).filter(
			(category) => category.toLowerCase() !== 'default',
		);
		return categories;
	}

	static fetchTabCategoryDetails(tab: string, category: string): Record<string, any> {
		const tabSections = SearchConfig._config?.ui?.tabSections ?? SearchConfig.tabsConfig;
		const tabSection = tabSections[tab];
		const categoryDetails = {
			...(tabSection?.categories?.default ?? {}),
			...(tabSection?.categories?.[category] ?? {}),
		};
		return categoryDetails;
	}
}
